import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { MapPin, ArrowRight } from "lucide-react";
import Reveal from "@/components/Reveal";

/**
 * Inside Di Organics Shoppe — compact cinematic interior section.
 * Copy on the left, video on the right (desktop). Flows directly out of
 * ShoppeTransitionChamber. No min-h-screen, no spacer wrappers.
 */
const VisitTheShoppe = () => {
  const sectionRef = useRef<HTMLElement | null>(null);
  const [shouldLoadVideo, setShouldLoadVideo] = useState(false);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node || typeof IntersectionObserver === "undefined") {
      setShouldLoadVideo(true);
      return;
    }
    const obs = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            setShouldLoadVideo(true);
            obs.disconnect();
          }
        });
      },
      { rootMargin: "300px 0px" },
    );
    obs.observe(node);
    return () => obs.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      aria-labelledby="inside-shoppe-heading"
      className="relative py-14 md:py-20 overflow-hidden texture-paper"
      style={{
        background:
          "linear-gradient(180deg, hsl(40 30% 95%) 0%, hsl(35 30% 93%) 100%)",
      }}
    >
      {/* faint grain */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-[0.05] mix-blend-overlay"
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='240' height='240'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.85' numOctaves='2'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")",
        }}
      />

      <div className="container mx-auto px-6 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 items-center">
          {/* Copy — left on desktop */}
          <Reveal className="lg:col-span-5 order-2 lg:order-1" delay={120}>
            <p className="font-sans text-[11px] tracking-[0.42em] uppercase text-accent mb-3 flex items-center gap-3">
              <span aria-hidden className="w-8 h-px bg-accent/60" />
              Inside the Shoppe
            </p>
            <h2
              id="inside-shoppe-heading"
              className="font-serif text-3xl md:text-[2.4rem] leading-[1.15] text-foreground mb-5"
            >
              A Living Caribbean House of Wellness, Beauty &amp; Memory
            </h2>
            <div className="space-y-4 font-sans text-base md:text-lg text-foreground/75 leading-relaxed mb-6">
              <p>
                Inside the old walls of Shadwell Great House lives a curated
                world of botanical beauty, island wellness, sensory ritual,
                and Caribbean craftsmanship.
              </p>
              <p>
                Shelves lined with herbal oils, slow-crafted skincare, teas,
                preserves, spices, and cultural memory invite visitors into a
                different rhythm — one rooted in heritage, healing, and island
                living.
              </p>
            </div>

            <blockquote
              className="my-6 pl-5 font-serif italic text-xl md:text-2xl text-foreground/85 leading-snug"
              style={{ borderLeft: "2px solid hsl(38 65% 55%)" }}
            >
              “More than a store. A living Caribbean apothecary.”
            </blockquote>

            <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 mt-7">
              <Link
                to="/visit-di-organics-shoppe"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-sans text-[11px] tracking-[0.3em] uppercase bg-primary text-primary-foreground hover:bg-primary/90 transition-colors"
              >
                Visit the Shoppe
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/virtual-shop"
                className="inline-flex items-center justify-center gap-2 px-6 py-3 rounded-full font-sans text-[11px] tracking-[0.3em] uppercase border border-foreground/30 text-foreground hover:border-accent hover:text-accent transition-colors"
              >
                Enter the Virtual Shop
              </Link>
            </div>
          </Reveal>

          {/* Video — right on desktop, top on mobile */}
          <Reveal className="lg:col-span-7 order-1 lg:order-2" duration={900}>
            <figure className="relative">
              <div
                className="relative w-full overflow-hidden"
                style={{
                  borderRadius: "20px",
                  aspectRatio: "4 / 5",
                  border: "1px solid hsl(38 55% 60% / 0.4)",
                  boxShadow:
                    "0 24px 60px -28px rgba(60,40,15,0.45), inset 0 0 0 1px hsl(38 55% 75% / 0.25)",
                  background: "hsl(150 20% 8%)",
                }}
              >
                {shouldLoadVideo && (
                  <video
                    src="/videos/di-organics-shoppe.mp4"
                    autoPlay
                    muted
                    loop
                    playsInline
                    preload="metadata"
                    aria-label="Atmospheric tour inside Di Organics Shoppe at Shadwell Great House"
                    className="absolute inset-0 w-full h-full object-cover"
                    style={{
                      filter: "saturate(1.05) contrast(1.03) brightness(0.98)",
                    }}
                  />
                )}
                <div
                  aria-hidden
                  className="absolute inset-0 pointer-events-none"
                  style={{
                    background:
                      "linear-gradient(180deg, hsla(28,40%,12%,0.10) 0%, transparent 45%, hsla(28,40%,12%,0.35) 100%)",
                  }}
                />
                <div className="absolute bottom-4 left-4">
                  <span
                    className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full font-sans text-[10px] tracking-[0.3em] uppercase text-[hsl(40_33%_95%)] backdrop-blur-sm"
                    style={{
                      background: "hsla(150, 25%, 10%, 0.55)",
                      border: "1px solid hsla(38, 65%, 70%, 0.4)",
                    }}
                  >
                    <MapPin className="w-3 h-3 text-[hsl(38_65%_70%)]" />
                    Shadwell Great House
                  </span>
                </div>
              </div>
            </figure>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

export default VisitTheShoppe;
